import {
    FaTools,
    FaUserCog,
    FaWallet,
    FaBolt
} from "react-icons/fa";
import { useRef, useEffect, useState } from "react";

// Intersection Observer hook for animation trigger
function useOnScreen(ref, rootMargin = "0px") {
    const [isIntersecting, setIntersecting] = useState(false);

    useEffect(() => {
        if (!ref.current) return;
        const observer = new window.IntersectionObserver(
            ([entry]) => setIntersecting(entry.isIntersecting),
            { rootMargin }
        );
        observer.observe(ref.current);
        return () => observer.disconnect();
    }, [ref, rootMargin]);

    return isIntersecting;
}

export default function WhyChooseUs() {
    const sectionRef = useRef(null);
    // Delay children animation for stagger effect
    const [hasAnimated, setHasAnimated] = useState(false);

    const isVisible = useOnScreen(sectionRef, "-80px");

    useEffect(() => {
        if (isVisible && !hasAnimated) setHasAnimated(true);
    }, [isVisible, hasAnimated]);

    const features = [
        {
            icon: <FaTools />,
            title: "Professional Service",
            desc: "We provide professional IT services including networking, CCTV installation, system maintenance and AMC support."
        },
        {
            icon: <FaUserCog />,
            title: "Experienced Technicians",
            desc: "Our skilled technicians have years of experience handling complex computer, server and networking systems."
        },
        {
            icon: <FaWallet />,
            title: "Affordable Price",
            desc: "We offer reliable IT and CCTV services at competitive and affordable pricing for businesses."
        },
        {
            icon: <FaBolt />,
            title: "Fast Support",
            desc: "Our team ensures quick response and fast troubleshooting to keep your systems running smoothly."
        }
    ];

    // You can swap this background image for another suited to the theme/branding
    const bgImage =
        "url('/img/img4.jpeg')";

    // Make vertical movement longer & duration slower for slow upward effect
    const headingInitialY = 70; // px
    const featureInitialY = 90; // px
    const taglineInitialY = 70; // px
    const transitionDuration = 1500; // ms

    return (
        <section
            ref={sectionRef}
            className="relative py-24 font-PTSerif-Regular"
            style={{
                backgroundImage: `${bgImage}`,
                backgroundAttachment: "fixed",
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
        >
            {/* Overlay for better contrast */}
            <div className="absolute inset-0 bg-white/60 pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6">
                {/* Heading */}
                <div
                    className="text-center mb-16 font-PTSerif-Regular transition-all"
                    style={{
                        transition: `all ${transitionDuration}ms cubic-bezier(0.33,0.75,0.68,1)`, // slower
                        transform: hasAnimated
                            ? "translateY(0)"
                            : `translateY(${headingInitialY}px)`,
                        opacity: hasAnimated ? 1 : 0,
                    }}
                >
                    <h2 className="text-4xl font-bold text-gray-900 font-PTSerif-Regular">
                        Why Choose Us
                    </h2>

                    <div className="w-16 h-[3px] bg-blue-500 mx-auto mt-3"></div>

                    <p className="text-gray-600 mt-4 max-w-2xl mx-auto font-PTSerif-Regular">
                        Stralia Pvt. Ltd. provides professional CCTV, IT and Networking
                        solutions for offices and businesses with reliable service and
                        expert technical support.
                    </p>

                </div>

                {/* Features Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 font-PTSerif-Regular">
                    {features.map((feature, index) => (
                        <div
                            key={index}
                            className={`
                                bg-white shadow-md hover:shadow-xl transition p-8 text-center rounded-lg font-PTSerif-Regular
                                transition-all
                            `}
                            style={{
                                transition: `all ${transitionDuration}ms cubic-bezier(0.33,0.75,0.68,1)`,
                                transitionDelay: hasAnimated ? `${0.5 + index * 0.18}s` : "0s",
                                transform: hasAnimated
                                    ? "translateY(0) scale(1)"
                                    : `translateY(${featureInitialY}px) scale(0.95)`,
                                opacity: hasAnimated ? 1 : 0,
                            }}
                        >
                            {/* Icon */}
                            <div className="text-blue-500 text-4xl mb-4 flex justify-center font-PTSerif-Regular">
                                {feature.icon}
                            </div>

                            {/* Title */}
                            <h3 className="text-lg font-semibold text-gray-800 mb-3 font-PTSerif-Regular">
                                {feature.title}
                            </h3>

                            {/* Description */}
                            <p className="text-sm text-gray-600 leading-relaxed font-PTSerif-Regular">
                                {feature.desc}
                            </p>

                        </div>
                    ))}
                </div>

                {/* Bottom Tagline */}
                <div
                    className="text-center mt-16 font-PTSerif-Regular transition-all"
                    style={{
                        transition: `all ${transitionDuration}ms cubic-bezier(0.33,0.75,0.68,1)`,
                        transitionDelay: hasAnimated ? "1.25s" : "0s",
                        transform: hasAnimated
                            ? "translateY(0)"
                            : `translateY(${taglineInitialY}px)`,
                        opacity: hasAnimated ? 1 : 0,
                    }}
                >
                    <h3 className="text-xl font-semibold text-gray-800 font-PTSerif-Regular">
                        STRALIA PRIVATE LIMITED
                    </h3>

                    <p className="text-blue-600 mt-2 font-medium font-PTSerif-Regular">
                        CCTV, IT & Networking Solutions
                    </p>
                </div>

            </div>
        </section>
    );
}